/*
 * Room Detail
 *   - Single-open accordion: opening one item closes the others
 *   - Gallery up/down arrows smooth-scroll the right column by one image height
 *   - Disable arrows at the scroll boundaries
 */

function initAccordions(root) {
    const wrap = root.querySelector('.js-room-detail-accordions');
    if (!wrap) return;
    const toggles = Array.from(wrap.querySelectorAll('.js-room-detail-accordion-toggle'));

    const panelOf = (toggle) => {
        const id = toggle.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;
    };

    const closePanel = (toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        const panel = panelOf(toggle);
        if (!panel) return;
        // Snap from natural height to 0 so the close transition runs from
        // the actual rendered size rather than from the unbounded value.
        panel.style.maxHeight = panel.scrollHeight + 'px';
        // Force reflow so the browser commits the explicit height before
        // we transition to 0.
        // eslint-disable-next-line no-unused-expressions
        panel.offsetHeight;
        panel.style.maxHeight = '0px';
    };

    const openPanel = (toggle) => {
        toggle.setAttribute('aria-expanded', 'true');
        const panel = panelOf(toggle);
        if (!panel) return;
        panel.style.maxHeight = panel.scrollHeight + 'px';
    };

    const closeAll = (except) => {
        toggles.forEach((t) => {
            if (t === except) return;
            if (t.getAttribute('aria-expanded') === 'true') closePanel(t);
        });
    };

    // After an open transition finishes, drop the explicit max-height so
    // the panel can grow if its content reflows (window resize, font swap).
    toggles.forEach((toggle) => {
        const panel = panelOf(toggle);
        if (!panel) return;
        panel.addEventListener('transitionend', (e) => {
            if (e.propertyName !== 'max-height') return;
            if (toggle.getAttribute('aria-expanded') === 'true') {
                panel.style.maxHeight = 'none';
            }
        });
    });

    toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closePanel(toggle);
            } else {
                closeAll(toggle);
                openPanel(toggle);
            }
        });
    });
}

function initGallery(root) {
    const track = root.querySelector('.js-room-detail-gallery-track');
    const up    = root.querySelector('.js-room-detail-gallery-up');
    const down  = root.querySelector('.js-room-detail-gallery-down');
    if (!track || (!up && !down)) return;

    const items = Array.from(track.children);
    if (items.length < 2) {
        if (up) up.disabled = true;
        if (down) down.disabled = true;
        return;
    }

    // Step = first image's height + the gap between items.
    const stepDistance = () => {
        const a = items[0].getBoundingClientRect();
        const b = items[1].getBoundingClientRect();
        return b.top - a.top;
    };

    const updateButtons = () => {
        const max = track.scrollHeight - track.clientHeight;
        if (up)   up.disabled   = track.scrollTop <= 1;
        if (down) down.disabled = track.scrollTop >= max - 1;
    };

    up?.addEventListener('click', () => {
        track.scrollBy({ top: -stepDistance(), behavior: 'smooth' });
    });
    down?.addEventListener('click', () => {
        track.scrollBy({ top: stepDistance(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);

    // Recompute when the track's box changes (covers lockGalleryHeight's
    // debounced resize-driven height update, which would otherwise leave
    // the buttons in stale state until the next scroll).
    if ('ResizeObserver' in window) {
        new ResizeObserver(updateButtons).observe(track);
    }

    // Recompute as each image loads — at DOM-ready the track's scrollHeight
    // is ~0 because images haven't decoded yet, which would otherwise leave
    // the down arrow disabled even when the stack is taller than the
    // viewport.
    track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load',  updateButtons, { once: true });
        img.addEventListener('error', updateButtons, { once: true });
    });

    // Final pass once everything (and lockGalleryHeight) has settled.
    if (document.readyState === 'complete') {
        requestAnimationFrame(updateButtons);
    } else {
        window.addEventListener('load', () => requestAnimationFrame(updateButtons), { once: true });
    }

    // Initial state.
    requestAnimationFrame(updateButtons);
}

/**
 * Lock the gallery's height to the LEFT column's natural height (with all
 * accordions closed). Without this, expanding an accordion grows the inner
 * grid row which in turn stretches the absolutely-positioned gallery —
 * making the carousel jump on every accordion interaction.
 */
function lockGalleryHeight(root) {
    const gallery = root.querySelector('.room-detail__gallery');
    const main    = root.querySelector('.room-detail__main');
    if (!gallery || !main) return;

    const measure = () => {
        // Drop any cached height so we can measure the natural state — also
        // releases the lock on viewports where the layout is single-column
        // and the gallery should size itself.
        gallery.style.height = '';
        // Only lock on the 2-column layout (>= 1000px). Below that the
        // gallery stacks below __main and CSS handles the mobile sizing.
        if (window.innerWidth < 1000) return;
        const h = main.getBoundingClientRect().height;
        if (h > 0) gallery.style.height = `${h}px`;
    };

    // Run after layout has settled (fonts/images roughly settled by load).
    if (document.readyState === 'complete') {
        measure();
    } else {
        window.addEventListener('load', measure, { once: true });
    }

    // Re-measure on resize so the gallery follows breakpoint changes.
    let resizeT = 0;
    window.addEventListener('resize', () => {
        clearTimeout(resizeT);
        resizeT = window.setTimeout(measure, 150);
    });
}

function initOne(root) {
    if (root.dataset.wired === '1') return;
    root.dataset.wired = '1';
    initAccordions(root);
    initGallery(root);
    lockGalleryHeight(root);
}

export function initRoomDetails() {
    document.querySelectorAll('.room-detail').forEach(initOne);
}
